import React, { useEffect, useState } from 'react'
import fetchWithAuth from '../../utils/fetchWithAuth'

type IngredientCatalogRecord = {
  id: number
  nameCanonical: string
  externalSource: string
}

interface Props {
  joinId: string | null
  isOpen: boolean
  onClose: () => void
  onMapped: () => void
}

export default function MapIngredientModal({
  joinId,
  isOpen,
  onClose,
  onMapped,
}: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<IngredientCatalogRecord[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // reset on open/close
  useEffect(() => {
    if (!isOpen) {
      setQuery('')
      setResults([])
      setSelectedId(null)
      setError('')
    }
  }, [isOpen])

  // fetch search results
  useEffect(() => {
    if (!query) return setResults([])
    const ctl = new AbortController()
    setLoading(true)
    fetchWithAuth(`/api/admin/ingredients/catalog?q=${encodeURIComponent(query)}`, {
      signal: ctl.signal,
    })
      .then((data) => setResults(data as IngredientCatalogRecord[]))
      .catch((err: any) => {
        if (err.name !== 'AbortError') setError('Search failed')
      })
      .finally(() => setLoading(false))
    return () => ctl.abort()
  }, [query])

  const handleMap = async () => {
    if (!joinId || !selectedId) return
    setError('')
    try {
      await fetchWithAuth(`/api/admin/ingredients/map`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ joinId, newCatalogId: selectedId }),
      })
      onMapped()
    } catch {
      setError('Mapping failed')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white max-w-md w-full p-6 rounded shadow-lg relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-600 hover:text-gray-900"
        >
          ✕
        </button>

        <h2 className="text-lg font-semibold mb-4">Map Ingredient</h2>
        {error && <p className="text-red-600 mb-2">{error}</p>}

        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search ingredient catalog…"
          className="w-full p-2 border rounded mb-2"
        />

        {loading && <p>Searching…</p>}

        {results.length > 0 && (
          <ul className="border rounded max-h-40 overflow-auto mb-4">
            {results.map(item => (
              <li
                key={item.id}
                className={`p-2 cursor-pointer ${
                  selectedId === item.id ? 'bg-blue-100' : 'hover:bg-gray-100'
                }`}
                onClick={() => setSelectedId(item.id)}
              >
                {item.nameCanonical}{' '}
                <span className="text-gray-500 text-sm">
                  (ID: {item.id})
                </span>
              </li>
            ))}
          </ul>
        )}

        <button
          onClick={handleMap}
          disabled={!selectedId}
          className="px-4 py-2 bg-green-600 text-white rounded disabled:opacity-50"
        >
          Map
        </button>
      </div>
    </div>
  )
}
